import React, {
  useCallback, useEffect, useState, useMemo, ReactElement,
} from 'react';
import { useNavigate } from 'react-router';
import Table from '../../../components/table/table.component';
import TableBody from '../../../components/table-body/table-body.component';
import TableRow from '../../../components/table-row/table-row.component';
import TableCell from '../../../components/table-cell/table-cell.component';
import Image from '../../../components/image/image.component';
import Widget from '../../../components/widget/widget.component';
import FilteredLink from '../../../components/filtered-link/filtered-link.component';
import TextSmallSize from '../../../components/text-small-size/text-small-size.component';
import LastUpdateBlock from '../../app/common/last-update-block/last-update-block.component';
import ActivityModal from '../../activity-list/activity-modal/activity-modal.component';
import ScreenService from '../../../services/screen-service';
import useScreenSmall from '../../../hooks/use-screen-small';
import { ROUTE_DEAL } from '../../../constants/system/routes';
import {
  ACTIVITY_ATTACHMENT_LIST_VIEW, ACTIVITY_COMMENT_LIST_VIEW, DEAL_VIEW,
} from '../../../constants/entity/permissions';
import { WIDGET_NAME_RECENT_DEAL_UPDATES } from '../../../constants/system/widget-names';
import AssetDefaultImage from '../../../static/images/asset-default-image.png';

const DashboardWidgetRecentDealUpdates: ReactMemoComponent = React.memo((props: ReactComponentProps): ReactElement => {
  const WIDGET_TITLE_DEFAULT: string = 'Recent Deal Updates';

  const {
    isActivityCommentSaving,
    dealListData,
    currentUser,
    widgetSettings,
    newCommentList,
    contactCompanyList,
    contactPersonList,
    contactLinkedUserList,
    tagList,
    onContactCompanySearch,
    onContactCompanySearchClose,
    onContactPersonSearch,
    onContactPersonSearchClose,
    onAddNewDealActivityComment,
    onUpdateNewDealActivityComment,
    onDeleteNewDealActivityComment,
    onCommentEntityLinkClick,
  } = props;

  const navigate: ReactRouterNavigate = useNavigate();
  const isSmallScreen: boolean = useScreenSmall();

  const [isNewDealActivityModalOpen, setIsNewDealActivityModalOpen]: [boolean, Function] = useState(false);
  const [newDealActivityItemData, setNewDealActivityItemData]: [Activity | null, Function]
    = useState<StateValueDefault>(null);

  const activityCommentListViewPermissions: string[] = useMemo((): string[] => [ACTIVITY_COMMENT_LIST_VIEW], []);
  const activityAttachmentListViewPermissions: string[] = useMemo((): string[] => [ACTIVITY_ATTACHMENT_LIST_VIEW], []);

  const onRowClick: Function = useCallback((dealId: number): void => {
    const withNoSelection: boolean = ScreenService.checkNoSelection();

    if (withNoSelection) {
      navigate({
        pathname: `${ROUTE_DEAL}/${dealId}`,
      });
    }
  }, []);

  const openNewDealActivityModal: Function = useCallback((newDealActivityItem: Activity, dealItem: Deal): void => {
    setNewDealActivityItemData({
      ...newDealActivityItem,
      deal: {
        id: dealItem.id,
        name: dealItem.name,
        mainPhoto: dealItem.mainPhoto,
        mainPhotoThumbnail: dealItem.mainPhotoThumbnail,
      },
    });
    setIsNewDealActivityModalOpen(true);
  }, []);

  const closeNewDealActivityModal: Function = useCallback((): void => {
    setIsNewDealActivityModalOpen(false);
    setNewDealActivityItemData(null);
  }, []);

  const onLastUpdateColumnClick: MouseEventItemHandlerFunction = useCallback((
    event: React.MouseEvent<Element, MouseEvent>, activityItem: Activity, dealItem: Deal,
  ): void => {
    if (activityItem && dealItem) {
      openNewDealActivityModal(activityItem, dealItem);
    }

    event.stopPropagation();
  }, []);

  useEffect((): void => {
    if (dealListData && newDealActivityItemData) {
      dealListData.forEach((dealItem: Deal): void => {
        if (dealItem.newDealActivity && dealItem.newDealActivity.id === newDealActivityItemData.id) {
          setNewDealActivityItemData({
            ...dealItem.newDealActivity,
            deal: {
              id: dealItem.id,
              name: dealItem.name,
              mainPhoto: dealItem.mainPhoto,
              mainPhotoThumbnail: dealItem.mainPhotoThumbnail,
            },
          });
        }
      });
    }
  }, [newCommentList]);

  return (
    <Widget
      isScrollable
      isEmpty={!dealListData || !dealListData.length}
      widgetName={WIDGET_NAME_RECENT_DEAL_UPDATES}
      defaultWidgetTitle={WIDGET_TITLE_DEFAULT}
      widgetSettings={widgetSettings}
    >
      <Table>
        <TableBody isPlain withZebraRows withHeaderColumn>
          {
            dealListData.map((dealItem: Deal): ReactElement => (
              <TableRow
                isButton
                key={dealItem.id}
                onClick={(): void => {
                  onRowClick(dealItem.id);
                }}
              >
                <TableCell width="5%">
                  <Image
                    text={dealItem.name}
                    link={dealItem.mainPhotoThumbnail || dealItem.mainPhoto || AssetDefaultImage}
                  />
                </TableCell>
                <TableCell width="55%">
                  <div>
                    <FilteredLink link={`${ROUTE_DEAL}/${dealItem.id}`} permission={DEAL_VIEW}>
                      {dealItem.name}
                    </FilteredLink>
                  </div>
                  {
                    !!dealItem.loanAmount && (
                      <TextSmallSize>
                        {`Loan amount: ${dealItem.loanAmount}`}
                      </TextSmallSize>
                    )
                  }
                </TableCell>
                <TableCell
                  width="40%"
                  onClick={(event: React.MouseEvent<Element, MouseEvent>): void => {
                    onLastUpdateColumnClick(event, dealItem.newDealActivity, dealItem);
                  }}
                >
                  {
                    dealItem.newDealActivity && (
                      <LastUpdateBlock
                        withSmallText
                        withWrap={isSmallScreen}
                        userText={dealItem.newDealActivity.updatedByUserText}
                        dateText={dealItem.newDealActivity.updatedDateText}
                        commentList={dealItem.newDealActivity.comments}
                        attachmentList={dealItem.newDealActivity.attachments}
                        commentListViewPermissions={activityCommentListViewPermissions}
                        attachmentListViewPermissions={activityAttachmentListViewPermissions}
                      />
                    )
                  }
                </TableCell>
              </TableRow>
            ))
          }
        </TableBody>
      </Table>
      <ActivityModal
        isDealActivity
        isOpen={isNewDealActivityModalOpen}
        isCommentSaving={isActivityCommentSaving}
        activityData={newDealActivityItemData}
        currentUser={currentUser}
        newCommentList={newCommentList}
        contactCompanyList={contactCompanyList}
        contactPersonList={contactPersonList}
        contactLinkedUserList={contactLinkedUserList}
        tagList={tagList}
        onCloseModal={closeNewDealActivityModal}
        onAddComment={onAddNewDealActivityComment}
        onUpdateComment={onUpdateNewDealActivityComment}
        onDeleteComment={onDeleteNewDealActivityComment}
        onContactCompanySearch={onContactCompanySearch}
        onContactCompanySearchClose={onContactCompanySearchClose}
        onContactPersonSearch={onContactPersonSearch}
        onContactPersonSearchClose={onContactPersonSearchClose}
        onCommentEntityLinkClick={onCommentEntityLinkClick}
      />
    </Widget>
  );
});

export default DashboardWidgetRecentDealUpdates;
